import { Component } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogContent,
  DialogBody,
  DialogCloseTrigger,
  DialogPositioner,
  Portal
} from '@chakra-ui/react'
import type { DialogRootProps } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { Spacer, ModalTitle } from 'stylewhere/components'

export interface ModalProps {
  title?: React.ReactNode
  visible?: boolean
  onClose: () => void
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full'
  fullContent?: boolean
  align?: 'left' | 'right' | 'center'
  padding?: string
  paddingDivContent?: number
  showCloseButton?: boolean
  closeOnOverlayClick?: boolean
  'data-testid'?: string
  minModalContentWidth?: number
  margin?: string
  children?: React.ReactNode
  closeOnEnter?: boolean
}

interface State {
  visible: boolean
}

export class Modal extends Component<ModalProps, State> {
  state: State = {
    visible: this.props.visible ?? false
  }

  show = () => {
    this.setState({ visible: true })
  }

  hide = () => {
    this.setState({ visible: false })
  }

  static getDerivedStateFromProps(nextProps) {
    return {
      visible: nextProps.visible
    }
  }

  componentDidMount() {
    if (this.props.closeOnEnter) {
      window.addEventListener('keydown', this.handleKeyDown, true)
    }
  }

  componentDidUpdate(prevProps: ModalProps) {
    if (this.props.closeOnEnter !== prevProps.closeOnEnter) {
      if (this.props.closeOnEnter) {
        window.addEventListener('keydown', this.handleKeyDown, true)
      } else {
        window.removeEventListener('keydown', this.handleKeyDown, true)
      }
    }
  }

  componentWillUnmount() {
    if (this.props.closeOnEnter) {
      window.removeEventListener('keydown', this.handleKeyDown, true)
    }
  }

  handleKeyDown = (e: KeyboardEvent) => {
    const { closeOnEnter, onClose } = this.props
    const { visible } = this.state
    
    if (e.key === 'Enter' && visible && closeOnEnter) {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' &&
        target.closest(`[data-testid="${this.props['data-testid'] || 'modal-content'}"]`)
      ) {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
  }

  render() {
    const {
      showCloseButton,
      closeOnOverlayClick = true,
      fullContent,
      title,
      children,
      align,
      padding,
      paddingDivContent,
      'data-testid': dataTestId,
      minModalContentWidth,
      margin,
      onClose
    } = this.props
    const { visible } = this.state
    
    if (!visible) return null

    return (
      <Dialog.Root
        open={visible}
        onOpenChange={(e) => {
          if (!e.open) {
            onClose()
          }
        }}
        modal={true}
        closeOnInteractOutside={closeOnOverlayClick}
        closeOnEscape={closeOnOverlayClick}
      >
        <Portal>
          <StyledBackdrop data-testid="modal-backdrop" />
          <Positioner>
            <Content
              minWidth={minModalContentWidth}
              data-testid={dataTestId || 'modal-content'}
              padding={padding}
              fullContent={fullContent}
              size={this.props.size}
              onClick={(e) => e.stopPropagation()}
            >
              {showCloseButton && (
                <CloseButtonWrapper>
                  <DialogCloseTrigger data-testid="modal-close-button">
                    <CloseIcon>×</CloseIcon>
                  </DialogCloseTrigger>
                </CloseButtonWrapper>
              )}
              {title && (
                <div data-testid="modal-title">
                  <ModalTitle align={align} title={title} />
                  <Spacer />
                </div>
              )}
              {!fullContent && (
                <Body style={{ textAlign: align || 'left', margin: margin }}>
                  <div
                    data-testid="modal-body"
                    style={{ padding: paddingDivContent !== undefined ? paddingDivContent : 3 }}
                  >
                    {children}
                  </div>
                </Body>
              )}
              {fullContent && <div data-testid="modal-body">{children}</div>}
            </Content>
          </Positioner>
        </Portal>
      </Dialog.Root>
    )
  }
}

const Body = styled(DialogBody)`
  overflow: auto;
`

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1001;
  cursor: pointer;
`

const CloseIcon = styled.span`
  display: inline-block;
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 28px;
  color: #666666;
  font-weight: 300;
  border-radius: 4px;

  &:hover {
    background-color: #f0f0f0;
    color: #333333;
  }
`

const StyledBackdrop = styled(DialogBackdrop)`
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 999;
  pointer-events: auto !important;
`

const Positioner = styled(DialogPositioner)`
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 1000;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const Content = styled(DialogContent)<{ fullContent?: boolean; padding?: string; size?: string }>`
  ${({ fullContent, padding }) =>
    !fullContent &&
    `
    padding: ${padding || '25px 32px'};
  `}
  overflow: hidden;
  position: relative;
  margin: 0;
  max-width: ${({ size }) => {
    const sizes: Record<string, string> = {
      xs: '20rem',
      sm: '24rem',
      md: '28rem',
      lg: '32rem',
      xl: '36rem',
      '2xl': '42rem',
      '3xl': '48rem',
      '4xl': '56rem',
      '5xl': '64rem',
      '6xl': '72rem',
      full: '100%'
    }
    return sizes[size || 'md'] || sizes.md
  }};
  max-height: 90vh;
  overflow-y: auto;
`
